import { commercialConfig } from "@/config/storeConfig";
import { getDeliveryMethodById, getFreeShippingRules } from "./siteContent";
import { formatBRL } from "./format";
import type { CheckoutForm, DeliveryMethodId, FreeShippingRule, ShippingResult } from "@/types";

export const onlyDigits = (value: string): string => value.replace(/\D/g, "");

export const normalizePhone = (value: string): string => onlyDigits(value);

export const formatPhoneBR = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export const formatCep = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const isValidPhone = (value: string): boolean => {
  const digits = onlyDigits(value);
  return digits.length === 10 || digits.length === 11;
};

export const isValidCep = (value: string): boolean => onlyDigits(value).length === 8;

const UF_LIST = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO",
];

export const isValidUF = (value: string): boolean => UF_LIST.includes(value.trim().toUpperCase());

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const nameHasLetters = (value: string): boolean => /[a-zA-ZÀ-ÿ]/.test(value);

export const normalize = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/* ----------------------------- Desconto ----------------------------- */

export const calcDiscount = (subtotal: number): number => {
  const { discount } = commercialConfig;
  if (!discount.enabled) return 0;
  return subtotal >= discount.threshold ? subtotal * discount.rate : 0;
};

export const discountLabel = commercialConfig.discount.label;

/* ------------------------- Frete grátis / entrega ------------------- */

const matchesRule = (rule: FreeShippingRule, subtotal: number, form: CheckoutForm): boolean => {
  if (typeof rule.minValue === "number" && subtotal < rule.minValue) return false;

  if (rule.cities?.length) {
    const city = normalize(form.address.city || form.city);
    if (!rule.cities.some((item) => normalize(item) === city)) return false;
  }

  if (rule.districts?.length) {
    const district = normalize(form.address.district);
    if (!rule.districts.some((item) => normalize(item) === district)) return false;
  }

  if (rule.cepRanges?.length) {
    const cep = onlyDigits(form.address.cep);
    const inside = rule.cepRanges.some((range) => {
      const from = onlyDigits(range.from);
      const to = onlyDigits(range.to);
      return cep.length === 8 && cep >= from && cep <= to;
    });
    if (!inside) return false;
  }

  if (rule.campaign) {
    const now = Date.now();
    const start = new Date(rule.campaign.start).getTime();
    const end = new Date(rule.campaign.end).getTime();
    if (Number.isFinite(start) && now < start) return false;
    if (Number.isFinite(end) && now > end) return false;
  }

  return true;
};

export const resolveShipping = (
  methodId: DeliveryMethodId | "",
  subtotal: number,
  form: CheckoutForm,
): ShippingResult => {
  const method = methodId ? getDeliveryMethodById(methodId) : undefined;
  if (!method) {
    return {
      fee: null,
      feeLabel: "—",
      freeApplied: false,
      message: "",
      applies: false,
    };
  }

  const rule = getFreeShippingRules().find(
    (item) => item.enabled && item.methodIds.includes(method.id) && matchesRule(item, subtotal, form),
  );

  if (rule) {
    return {
      fee: 0,
      feeLabel: formatBRL(0),
      freeApplied: true,
      ruleLabel: rule.label,
      message: "Você ganhou frete grátis na entrega local.",
      applies: true,
    };
  }

  if (method.fee === null) {
    return {
      fee: null,
      feeLabel: "Taxa a confirmar",
      freeApplied: false,
      message: "",
      applies: false,
    };
  }

  return {
    fee: method.fee,
    feeLabel: formatBRL(method.fee),
    freeApplied: false,
    message: "",
    applies: false,
  };
};

/** Progresso de frete grátis para a primeira regra habilitada. */
export const getFreeShippingProgress = (
  subtotal: number,
): { hasRule: boolean; eligible: boolean; remaining: number; message: string; ruleLabel: string } => {
  const rule = getFreeShippingRules().find((item) => item.enabled && typeof item.minValue === "number");
  if (!rule || typeof rule.minValue !== "number") {
    return { hasRule: false, eligible: false, remaining: 0, message: "", ruleLabel: "" };
  }
  const eligible = subtotal >= rule.minValue;
  const remaining = Math.max(0, rule.minValue - subtotal);
  return {
    hasRule: true,
    eligible,
    remaining,
    ruleLabel: rule.label,
    message: eligible
      ? "Você ganhou frete grátis na entrega local."
      : `Faltam ${formatBRL(remaining)} para você ganhar frete grátis na entrega local.`,
  };
};

/* ----------------------------- Validação ---------------------------- */

export type CheckoutErrors = Record<string, string>;

export const validateStep = (step: number, form: CheckoutForm): CheckoutErrors => {
  const errors: CheckoutErrors = {};

  if (step === 0) {
    if (form.name.trim().length < 3) {
      errors.name = "Informe seu nome completo.";
    } else if (!nameHasLetters(form.name)) {
      errors.name = "O nome não pode conter apenas números.";
    }
    if (!isValidPhone(form.phone)) {
      errors.phone = "Informe um WhatsApp válido com DDD.";
    }
    if (form.email.trim() && !isValidEmail(form.email)) {
      errors.email = "Confira o e-mail informado.";
    }
    if (!form.city.trim()) {
      errors.city = "Informe a sua cidade.";
    }
    if (!isValidUF(form.state)) {
      errors.state = "Use a sigla do estado, como GO ou SP.";
    }
    if (!form.consent) {
      errors.consent = "Precisamos da sua autorização para continuar.";
    }
  }

  if (step === 1) {
    const method = form.deliveryMethod ? getDeliveryMethodById(form.deliveryMethod) : undefined;
    if (method?.requiresCep && !isValidCep(form.address.cep)) {
      errors.cep = "Informe um CEP válido com 8 dígitos.";
    }
    if (method?.requiresAddress) {
      if (!form.address.street.trim()) errors.street = "Informe a rua ou avenida.";
      if (!form.address.number.trim()) errors.number = "Informe o número.";
      if (!form.address.district.trim()) errors.district = "Informe o bairro.";
    }
  }

  if (step === 2) {
    if (!form.deliveryMethod) {
      errors.deliveryMethod = "Escolha a forma de recebimento.";
    }
    const method = form.deliveryMethod ? getDeliveryMethodById(form.deliveryMethod) : undefined;
    if (method?.requiresBus) {
      if (!form.bus.destinationCity.trim()) errors.destinationCity = "Informe a cidade de destino.";
      if (!isValidUF(form.bus.destinationState)) {
        errors.destinationState = "Use a sigla do estado de destino.";
      }
      if (!form.bus.terminal.trim()) {
        errors.terminal = "Informe a rodoviária ou o ponto de retirada.";
      }
      if (form.bus.responsibleName.trim().length < 3) {
        errors.responsibleName = "Informe o nome de quem vai retirar.";
      }
      if (!isValidPhone(form.bus.responsiblePhone)) {
        errors.responsiblePhone = "Informe um telefone válido do responsável.";
      }
    }
  }

  return errors;
};

export const hasErrors = (errors: CheckoutErrors): boolean => Object.keys(errors).length > 0;